import React, { useEffect, useState } from 'react';
import { 
  Box, 
  Typography, 
  Card, 
  CardContent, 
  Grid, 
  Chip,
  ImageList,
  ImageListItem,
  Button,
  TextField,
  Switch,
  FormControlLabel,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Select,
  MenuItem,
  InputLabel,
  FormControl,
  Autocomplete
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import axios from 'axios';
import { useParams } from 'react-router-dom';
// Assume these come from an API or are predefined
const categoryOptions = ["Category 1", "Category 2", "Category 3"];
const subCategory1Options = ["Sub 1A", "Sub 1B", "Sub 1C"];
const subCategory2Options = ["Sub 2A", "Sub 2B", "Sub 2C"];
const tagOptions = ["Tag1", "Tag2", "Tag3", "Tag4", "Tag5"];

const ProductDetails = () => {
  const { product_id } = useParams();
  const [product, setProduct] = useState(null);
  const [editedProduct, setEditedProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isEditing, setIsEditing] = useState(false);

  useEffect(() => {
    const fetchProductDetails = async () => {
      try {
        const response = await axios.get(`https://cheebo-server.neuralbey.com/dashboard/store/productDetailsList/${product_id}/`);
        setProduct(response.data[0]);
        setEditedProduct(JSON.parse(JSON.stringify(response.data[0])));
      } catch (error) {
        console.error("Error fetching product details:", error);
      } finally {
        setLoading(false);
      }
    };

    if (product_id) {
      fetchProductDetails();
    }
  }, [product_id]);

  const handleEdit = () => setIsEditing(true);
  const handleCancel = () => {
    setEditedProduct(JSON.parse(JSON.stringify(product)));
    setIsEditing(false);
  };

  const handleSave = async () => {
    try {
      // Implement your API call to save the edited product here
      await axios.post(`https://cheebo-server.neuralbey.com/dashboard/store/productEdit/${product_id}/`, editedProduct);
      setProduct(editedProduct);
      setIsEditing(false);
    } catch (error) {
      console.error("Error saving product details:", error);
    }
  };

  const handleChange = (e, field, nestedField = null) => {
    const { value } = e.target;
    if (nestedField) {
      setEditedProduct(prev => ({
        ...prev,
        [field]: { ...prev[field], [nestedField]: value }
      }));
    } else {
      setEditedProduct(prev => ({ ...prev, [field]: value }));
    }
  };

  const renderEditField = (field, value, label, type = "text", nested = false) => (
    <TextField
      fullWidth
      name={field}
      label={label}
      value={value || ''}
      onChange={(e) => nested ? handleChange(e, 'product_information', field) : handleChange(e, field)}
      margin="normal"
      type={type}
      multiline={type === "multiline"}
      rows={type === "multiline" ? 4 : 1}
    />
  );

  const renderSelectField = (field, value, label, options) => (
    <FormControl fullWidth margin="normal">
      <InputLabel id={`${field}-label`}>{label}</InputLabel>
      <Select
        labelId={`${field}-label`}
        id={field}
        value={value || ''}
        label={label}
        onChange={(e) => handleChange(e, field)}
      >
        {options.map((option) => (
          <MenuItem key={option} value={option}>{option}</MenuItem>
        ))}
      </Select>
    </FormControl>
  );

  if (loading) return <Typography>Loading...</Typography>;
  if (!product) return <Typography>No product found.</Typography>;

  return (
    <Box sx={{ maxWidth: 1200, margin: 'auto', marginTop: '20px', padding: '20px' }}>
      <Grid container spacing={4}>
        <Grid item xs={12} md={6}>
          <Card>
            <img 
              src={`https://cheebo-server.neuralbey.com/${product.image}`}
              alt={product.title}
              style={{ width: '100%', height: 'auto', objectFit: 'cover' }}
            />
          </Card>
          {product.images && product.images.length > 0 && (
            <ImageList sx={{ width: '100%', height: 'auto', marginTop: 2 }} cols={3} rowHeight={164}>
              {product.images.map((item, index) => (
                console.log(item),
                <ImageListItem key={index}>
                  <img
                    src={`https://cheebo-server.neuralbey.com/${item['image']}`}
                    alt={`Product image ${index + 1}`}
                    loading="lazy"
                    style={{ objectFit: 'cover' }}
                  />
                </ImageListItem>
              ))}
            </ImageList>
          )}
        </Grid>
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              {isEditing ? (
                <>
                  {renderEditField("title", editedProduct.title, "Title")}
                  {renderEditField("price", editedProduct.price, "Price", "number")}
                  {renderEditField("description", editedProduct.description, "Description", "multiline")}
                  {renderEditField("seller", editedProduct.seller, "Seller")}
                  <FormControlLabel
                    control={
                      <Switch
                        checked={editedProduct.availability}
                        onChange={(e) => setEditedProduct(prev => ({ ...prev, availability: e.target.checked }))}
                        color='secondary'
                        name="availability"
                      />
                    }
                    label="Available"
                  />
                  {renderEditField("discount", editedProduct.discount, "Discount (%)", "number")}
                  <Autocomplete
                    multiple
                    id="tags"
                    options={tagOptions}
                    value={editedProduct.tags || []}
                    onChange={(event, newValue) => {
                      setEditedProduct(prev => ({ ...prev, tags: newValue }));
                    }}
                    renderInput={(params) => (
                      <TextField {...params} label="Tags" placeholder="Select or add tags" />
                    )}
                    freeSolo
                    sx={{ mt: 2 }}
                  />
                </>
              ) : (
                <>
                  <Typography variant="h4" component="h1" gutterBottom>
                    {product.title}
                  </Typography>
                  <Typography variant="h5" color="white" gutterBottom>
                    {product.price} dt
                    {product.discount > 0 && (
                      <Typography component="span" color="error" sx={{ ml: 1 }}>
                        ({product.discount}% off)
                      </Typography>
                    )}
                  </Typography>
                  <Typography variant="body1" paragraph>
                    {product.description}
                  </Typography>
                  <Typography variant="subtitle1" gutterBottom>
                    Seller: {product.seller}
                  </Typography>
                  <Chip 
                    label={product.availability ? 'In Stock' : 'Out of Stock'} 
                    color={product.availability ? 'success' : 'error'}
                    sx={{ marginBottom: 2 }}
                  />
                  <Box sx={{ display: 'flex', alignItems: 'center', marginBottom: 2 }}>
                    <Typography variant="subtitle1" sx={{ marginRight: 2 }}>
                      Average Rating: {product.average_rating ? product.average_rating.toFixed(1) : 'N/A'}
                    </Typography>
                    <Typography variant="subtitle1">
                      ({product.reviews_count} {product.reviews_count === 1 ? 'review' : 'reviews'})
                    </Typography>
                  </Box>
                  <Box sx={{ mt: 2 }}>
                    {product.tags && product.tags.map((tag) => (
                      <Chip key={tag} label={tag} sx={{ mr: 1, mb: 1 }} />
                    ))}
                  </Box>
                </>
              )}

              <Accordion>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Typography>Additional Details</Typography>
                </AccordionSummary>
                <AccordionDetails>
                  {isEditing ? (
                    <>
                      {renderSelectField("category", editedProduct.category, "Category", categoryOptions)}
                      {renderSelectField("subCategory1", editedProduct.subCategory1, "Sub-Category 1", subCategory1Options)}
                      {renderSelectField("subCategory2", editedProduct.subCategory2, "Sub-Category 2", subCategory2Options)}
                      {renderEditField("brand", editedProduct.brand, "Brand")}
                      {renderEditField("weight", editedProduct.product_information?.weight, "Weight", "text", true)}
                      {renderEditField("color", editedProduct.product_information?.color, "Color", "text", true)}
                      {Object.entries(editedProduct.product_information || {}).map(([key, value]) => (
                        key !== 'weight' && key !== 'color' && renderEditField(key, value, key, "text", true)
                      ))}
                    </>
                  ) : (
                    <>
                      <Typography><strong>Category:</strong> {product.category || 'N/A'}</Typography>
                      <Typography><strong>Sub-Category 1:</strong> {product.subCategory1 || 'N/A'}</Typography>
                      <Typography><strong>Sub-Category 2:</strong> {product.subCategory2 || 'N/A'}</Typography>
                      <Typography><strong>Brand:</strong> {product.brand || 'N/A'}</Typography>
                      <Typography><strong>Weight:</strong> {product.product_information?.weight || 'N/A'}</Typography>
                      <Typography><strong>Color:</strong> {product.product_information?.color || 'N/A'}</Typography>
                      {Object.entries(product.product_information || {}).map(([key, value]) => (
                        key !== 'weight' && key !== 'color' && <Typography key={key}><strong>{key}:</strong> {value || 'N/A'}</Typography>
                      ))}
                    </>
                  )}
                </AccordionDetails>
              </Accordion>

              <Box sx={{ mt: 2 }}>
                {isEditing ? (
                  <>
                    <Button color="secondary" variant="contained" onClick={handleSave} sx={{ mr: 1 }}>Save</Button>
                    <Button color="secondary" variant="outlined" onClick={handleCancel}>Cancel</Button>
                  </>
                ) : (
                  <Button color="secondary" variant="contained" onClick={handleEdit}>Edit</Button>
                )}
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};

export default ProductDetails;